import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Input, CardActions, Switch } from 'react-toolbox';
import Card from 'components/Card';
import cx from 'classnames';
import StyledSelect from 'components/StyledSelect';
import MessageBox from 'components/MessageBox';
import PhoneInput from 'components/PhoneType/PhoneInput';
import ColoredButton from 'components/ColoredButton';
import FlexBox from 'flexbox-react';
import { ThemeProvider } from 'react-css-themr';
import styles from 'styles/form';
import RTInput from 'styles/input';
import RTDropdown from 'styles/dropdown';
import RTCard from 'styles/card';
import { error } from '../../../../../utils';
import RTSwitch from 'styles/switch';
import WorkroomType from 'types/workroom';
import { STATES, TIMEZONES, VENDORS } from 'config/constants';
import { can } from '../../../../../services/ability';

const theme = { RTInput, RTDropdown, RTCard, RTSwitch };
const mediumInput = cx({ [styles.inputs]: true, [styles.largeInput]: true });
const cancelClass = cx([styles.cancelButton, styles.actionButton]);
const STATE_OPTIONS = STATES.map(state => ({ value: state, label: state }));

class WorkroomForm extends Component {
  constructor(props) {
    super(props);
    this.handleSave = this.handleSave.bind(this);
    this.handleActive = this.handleChange.bind(this, 'active');
    this.handleIhq = this.handleChange.bind(this, 'ihq');
    this.handleName = this.handleChange.bind(this, 'name');
    this.handleAccountingId = this.handleChange.bind(this, 'accounting_id');
    this.handleInventoryId = this.handleChange.bind(this, 'inventory_id');
    this.handleAddress = this.handleChange.bind(this, 'address');
    this.handleCity = this.handleChange.bind(this, 'city');
    this.handleState = this.handleChange.bind(this, 'state');
    this.handleZip = this.handleChange.bind(this, 'zip_code');
    this.handlePhoneNumber = this.handleChange.bind(this, 'phone_number');
    this.handleTimezone = this.handleChange.bind(this, 'timezone');
    this.timezonesSource = TIMEZONES.map(tz => ({ value: tz, label: tz }));
    this.handleVendor = this.handleChange.bind(this, 'vendor');
    this.vendorsSource = VENDORS.map(v => ({ value: v, label: v }));
    const { workroom } = this.props;
    this.state = { workroom: (workroom && { ...workroom }) ||
      { active: true, ihq: false } };
  }

  handleChange = (key, value) => {
    const workroom = this.state.workroom;
    workroom[key] = value;
    this.setState({ workroom });
  }

  handleCancel = () => this.props.onCancel();

  handleSave = () => this.props.onSave({ ...this.state.workroom });

  renderTitle = (workroom, isEdit, vendor) => {
    const entityLabel = vendor === 'IFI' ? 'District' : 'Workroom';
    const title = isEdit ? (workroom.name || `Edit ${entityLabel}`) : `Add ${entityLabel}`;
    const switchClassNames = cx({
      [styles.switchTitleAdd]: !isEdit,
      [styles.switchTitle]: true,
    });
    return (<div>
      <FlexBox justifyContent="space-between" className={styles.divider}>
        <div>
          <h5 className={styles.cardTitle}>{title}</h5>
          <p className={styles.cardSubtitle}>
            Please ensure that all fields contain accurate information
          </p>
        </div>
        <div>
          <Switch
            name="active"
            label={vendor === 'IFI' ? 'District Active' : 'Workroom Active'}
            onChange={this.handleActive}
            className={switchClassNames}
            checked={workroom.active}
          />
          <Switch
            name="ihq"
            label="IHQ"
            onChange={this.handleIhq}
            className={switchClassNames}
            checked={workroom.ihq || false}
          />
        </div>
      </FlexBox>
    </div>);
  }

  render = () => {
    const { workroom } = this.state;
    const { errors, role } = this.props;
    const { vendor } = workroom;
    const isEdit = !!workroom.id;
    const gError = error('global', errors);
    return (<ThemeProvider theme={theme}>
      <Card>
        {this.renderTitle(workroom, isEdit, vendor)}
        {gError ? <MessageBox error message={gError} /> : null}
        <FlexBox flexWrap="wrap" className={styles.divider}>
          {can(role, 'manage', 'manage_vendors') && <StyledSelect
            className={mediumInput}
            label="Vendor"
            onChange={this.handleVendor}
            options={this.vendorsSource}
            value={workroom.vendor}
            placeholder={'Select Vendor'}
            error={error('vendor', errors)}
          />}
          <Input
            label="Name"
            className={mediumInput}
            value={workroom.name}
            onChange={this.handleName}
            error={error('name', errors)}
            required
          />
          <Input
            label="Accounting ID"
            className={mediumInput}
            value={workroom.accounting_id}
            onChange={this.handleAccountingId}
            error={error('accounting_id', errors)}
            required
          />
          <Input
            label="Inventory ID"
            className={mediumInput}
            value={workroom.inventory_id}
            onChange={this.handleInventoryId}
            error={error('inventory_id', errors)}
            required
          />
        </FlexBox>
        <FlexBox flexWrap="wrap" className={styles.divider}>
          <Input
            label="Address"
            value={workroom.address}
            className={mediumInput}
            onChange={this.handleAddress}
            error={error('address', errors)}
          />
          <Input
            label="City"
            value={workroom.city}
            className={mediumInput}
            onChange={this.handleCity}
            error={error('city', errors)}
          />
          <StyledSelect
            className={mediumInput}
            label="State"
            onChange={this.handleState}
            options={STATE_OPTIONS}
            value={workroom.state}
            placeholder={'Select State'}
            error={error('state', errors)}
          />
          <Input
            label="Zip Code"
            value={workroom.zip_code}
            className={mediumInput}
            onChange={this.handleZip}
            error={error('zip_code', errors)}
          />
          <StyledSelect
            className={mediumInput}
            label="Timezone"
            onChange={this.handleTimezone}
            options={this.timezonesSource}
            value={workroom.timezone}
            placeholder={'Select Timezone'}
            error={error('timezone', errors)}
          />
          <PhoneInput
            label="Phone Number"
            type="tel"
            value={workroom.phone_number}
            className={mediumInput}
            onChange={this.handlePhoneNumber}
            error={error('phone_number', errors)}
          />
        </FlexBox>
        <FlexBox justifyContent="flex-end" flexWrap="wrap">
          <CardActions className={styles.actionsWrapper}>
            <ColoredButton
              className={cancelClass}
              responsive
              color="gray"
              label="Cancel"
              onClick={this.handleCancel}
            />
            <ColoredButton
              className={styles.actionButton}
              responsive
              color="green"
              label={isEdit ? 'Save Changes' : 'Add workroom'}
              onClick={this.handleSave}
            />
          </CardActions>
        </FlexBox>
      </Card>
    </ThemeProvider>);
  }
}

WorkroomForm.propTypes = {
  errors: PropTypes.object,
  workroom: WorkroomType,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  role: PropTypes.string.isRequired,
};

export default WorkroomForm;



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/workrooms/components/workroomForm.jsx